import { Box, Button, FormLabel, SvgIcon } from '@mui/material';
import TextField from '@mui/material/TextField';
import type React from 'react';
import { useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon from '../../utils/getMuiIcon';
import { useId } from '../../utils/useId';

type LabelledTextFieldProps = {
	label?: string;
	value?: string;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder?: string;
	dataTestId?: string;
	name?: string;
	isPasswordTextField?: boolean;
	inputRef?: React.Ref<HTMLInputElement>;
	error?: FieldError;
	multiline?: boolean;
	maxLength?: number;
	minRows?: number;
	required?: boolean;
	'aria-describedby'?: string;
};

const LabelledTextField: React.FC<LabelledTextFieldProps> = (props) => {
	const { t } = useTranslation();
	const [showPassword, setShowPassword] = useState(false);
	const {
		label,
		value,
		onChange,
		placeholder,
		dataTestId,
		name,
		isPasswordTextField,
		inputRef,
		error,
		multiline,
		maxLength,
		minRows = 2,
		required,
		'aria-describedby': ariaDescribedBy,
	} = props;

	const errorMessage = error?.message?.toString() ?? '';
	const fieldId = useId(name);
	const labelId = `${fieldId}-label`;
	const errorId = `${fieldId}-error`;
	const helperTextId = error ? errorId : undefined;
	const describedBy = [ariaDescribedBy, helperTextId].filter(Boolean).join(' ') || undefined;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			{label && (
				<FormLabel id={labelId} htmlFor={fieldId} sx={{ color: palette.text.primary }}>
					{label}
				</FormLabel>
			)}
			<TextField
				id={fieldId}
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				type={isPasswordTextField && !showPassword ? 'password' : 'text'}
				name={name}
				multiline={multiline}
				variant="outlined"
				inputRef={inputRef}
				helperText={t(errorMessage)}
				error={error ? true : false}
				minRows={multiline ? minRows : undefined}
				required={required}
				inputProps={{
					maxLength: maxLength,
					'aria-labelledby': label ? labelId : undefined,
					'aria-describedby': describedBy,
					'aria-invalid': error ? true : undefined,
					'aria-required': required ? true : undefined,
					style: multiline
						? {
								padding: 0,
							}
						: {
								paddingTop: spaces.xs,
								paddingBottom: spaces.xs,
								paddingLeft: spaces.sm,
								paddingRight: isPasswordTextField ? 0 : spaces.sm,
							},
					'data-testid': dataTestId,
				}}
				FormHelperTextProps={{
					id: helperTextId,
					role: error ? 'alert' : undefined,
				}}
				sx={{
					'& .MuiOutlinedInput-root': {
						paddingRight: isPasswordTextField ? 0 : spaces.sm,
					},
				}}
				InputProps={{
					endAdornment: isPasswordTextField ? (
						<Button
							onClick={() => setShowPassword(!showPassword)}
							aria-label={showPassword ? t('Hide password') : t('Show password')}
							aria-pressed={showPassword}
							type="button"
						>
							{showPassword ? (
								<SvgIcon component={getMuiIcon('eyeClosed')} sx={{ padding: 0 }} aria-hidden="true" />
							) : (
								<SvgIcon component={getMuiIcon('eyeOpen')} sx={{ padding: 0 }} aria-hidden="true" />
							)}
						</Button>
					) : null,
					sx: {
						borderRadius: borderRadius.sm,
						paddingRight: isPasswordTextField ? spaces.sm : 0,
						padding: multiline ? `${spaces.xs} ${spaces.sm}` : undefined,
					},
				}}
			/>
		</Box>
	);
};

export default LabelledTextField;
